/**
 * Histórico de snapshots da tabela + premiação após cada jogo encerrado.
 * Garantia local contra erros futuros — persiste em localStorage e IndexedDB.
 */
import { CALENDARIO_FASE_GRUPOS, inicioJogoMs } from '../data/calendarioCopa2026';
import { JOGOS_COPA_2026, nomeTime } from '../data/copaMundo2026';
import { JOGOS_MATA_MATA, NOME_FASE, type FaseMataMata } from '../data/mataMataCopa2026';
import {
  placarEmpatadoMM,
  placarValido,
  pontuacaoParticipante,
  type PlacaresMap,
  type VencedoresPenaltisMMMap,
} from './copaCalculo';
import { calcularPremiacao } from './copaPremiacao';

const STORAGE_KEY = 'copa-historico-bolao-ade';
const IDB_NAME = 'ade-copa-db';
const IDB_STORE = 'copaEstado';
const IDB_KEY = 'historicoBolao';

export interface LinhaHistoricoBolao {
  id: string;
  nome: string;
  setor: string;
  pontos: number;
  posicao: number;
  faltas: number;
  premioBase: number;
  premioFinal: number;
}

export interface SnapshotHistoricoBolao {
  id: string;
  eventoId: string;
  jogoId: string;
  jogoRotulo: string;
  jogoDetalhe: string;
  placarReal: { mandante: number; visitante: number };
  fase: 'grupos' | 'matamata';
  criadoEm: string;
  totalJogosComResultado: number;
  linhas: LinhaHistoricoBolao[];
}

export interface ParticipanteHistoricoInput {
  id: string;
  nome: string;
  setor?: string;
  faltas?: number;
  palpites: PlacaresMap;
}

interface EventoJogoHistorico {
  eventoId: string;
  jogoId: string;
  tipo: 'placar' | 'penaltis';
  ordem: number;
}

function numeroFifaJogo(jogoId: string): number {
  const cal = CALENDARIO_FASE_GRUPOS[jogoId];
  if (cal?.numeroFifa) return cal.numeroFifa;
  const idx = JOGOS_MATA_MATA.findIndex((j) => j.id === jogoId);
  if (idx >= 0) return 73 + idx;
  return 999;
}

function formatarDataJogo(jogoId: string): string {
  const cal = CALENDARIO_FASE_GRUPOS[jogoId];
  if (cal?.dataHoraBrt) {
    try {
      const d = new Date(cal.dataHoraBrt);
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    } catch {
      /* ignore */
    }
  }
  const ms = inicioJogoMs(jogoId);
  if (Number.isFinite(ms) && ms !== Infinity) {
    return new Date(ms).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  }
  return '';
}

export function rotuloJogoHistorico(
  jogoId: string,
  reais: PlacaresMap,
  penaltis: VencedoresPenaltisMMMap = {}
): { rotulo: string; detalhe: string; fase: 'grupos' | 'matamata' } {
  const p = reais[jogoId];
  const placarTxt =
    p && placarValido(p) ? ` · ${p.mandante}×${p.visitante}` : '';
  const penTxt =
    p && placarEmpatadoMM(p) && penaltis[jogoId]
      ? ` (pênaltis: ${penaltis[jogoId] === 'mandante' ? 'mandante' : 'visitante'})`
      : '';

  const jGrupo = JOGOS_COPA_2026.find((j) => j.id === jogoId);
  if (jGrupo) {
    const data = formatarDataJogo(jogoId);
    return {
      rotulo: `${nomeTime(jGrupo.mandanteId)} × ${nomeTime(jGrupo.visitanteId)}${placarTxt}`,
      detalhe: `Jogo FIFA #${numeroFifaJogo(jogoId)}${data ? ` · ${data}` : ''} · Grupo ${jGrupo.grupoId}`,
      fase: 'grupos',
    };
  }

  const jMm = JOGOS_MATA_MATA.find((j) => j.id === jogoId);
  if (jMm) {
    return {
      rotulo: `${NOME_FASE[jMm.fase as FaseMataMata]}${placarTxt}${penTxt}`,
      detalhe: `Jogo FIFA #${numeroFifaJogo(jogoId)} · Mata-mata`,
      fase: 'matamata',
    };
  }

  return { rotulo: jogoId + placarTxt, detalhe: jogoId, fase: 'grupos' };
}

function listarEventosAtuais(
  reais: PlacaresMap,
  penaltis: VencedoresPenaltisMMMap
): EventoJogoHistorico[] {
  const out: EventoJogoHistorico[] = [];

  JOGOS_COPA_2026.forEach((j) => {
    if (!placarValido(reais[j.id])) return;
    out.push({
      eventoId: `${j.id}:placar`,
      jogoId: j.id,
      tipo: 'placar',
      ordem: numeroFifaJogo(j.id) * 10,
    });
  });

  JOGOS_MATA_MATA.forEach((j) => {
    const r = reais[j.id];
    if (!placarValido(r)) return;
    out.push({
      eventoId: `${j.id}:placar`,
      jogoId: j.id,
      tipo: 'placar',
      ordem: numeroFifaJogo(j.id) * 10,
    });
    if (placarEmpatadoMM(r) && penaltis[j.id]) {
      out.push({
        eventoId: `${j.id}:penaltis`,
        jogoId: j.id,
        tipo: 'penaltis',
        ordem: numeroFifaJogo(j.id) * 10 + 1,
      });
    }
  });

  return out.sort((a, b) => a.ordem - b.ordem);
}

function reaisAteEvento(
  reaisCompletos: PlacaresMap,
  penaltisCompletos: VencedoresPenaltisMMMap,
  eventos: EventoJogoHistorico[],
  ateEventoId: string
): { reais: PlacaresMap; penaltis: VencedoresPenaltisMMMap } {
  const reais: PlacaresMap = {};
  const penaltis: VencedoresPenaltisMMMap = {};
  for (const ev of eventos) {
    if (ev.tipo === 'placar' && placarValido(reaisCompletos[ev.jogoId])) {
      reais[ev.jogoId] = reaisCompletos[ev.jogoId]!;
    }
    if (ev.tipo === 'penaltis' && penaltisCompletos[ev.jogoId]) {
      penaltis[ev.jogoId] = penaltisCompletos[ev.jogoId]!;
    }
    if (ev.eventoId === ateEventoId) break;
  }
  return { reais, penaltis };
}

function contarJogosComResultado(reais: PlacaresMap): number {
  let n = 0;
  JOGOS_COPA_2026.forEach((j) => {
    if (placarValido(reais[j.id])) n++;
  });
  JOGOS_MATA_MATA.forEach((j) => {
    if (placarValido(reais[j.id])) n++;
  });
  return n;
}

export function calcularLinhasHistorico(
  participantes: ParticipanteHistoricoInput[],
  reais: PlacaresMap,
  penaltis: VencedoresPenaltisMMMap
): LinhaHistoricoBolao[] {
  if (participantes.length === 0) return [];

  const placar = participantes
    .map((p) => ({
      p,
      calc: pontuacaoParticipante(
        p.palpites,
        reais,
        JOGOS_COPA_2026,
        JOGOS_MATA_MATA,
        penaltis
      ),
    }))
    .sort((a, b) => b.calc.total - a.calc.total);

  const entradas = placar.map((x) => ({
    id: x.p.id,
    nome: x.p.nome,
    faltas: x.p.faltas ?? 0,
    pontos: x.calc.total,
  }));
  const prem = calcularPremiacao(entradas);

  return placar.map((item, idx) => ({
    id: item.p.id,
    nome: item.p.nome,
    setor: item.p.setor || '',
    pontos: item.calc.total,
    posicao: idx + 1,
    faltas: item.p.faltas ?? 0,
    premioBase: prem[idx]?.premioBase ?? 0,
    premioFinal: prem[idx]?.premioFinal ?? 0,
  }));
}

export function carregarHistoricoBolao(): SnapshotHistoricoBolao[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as SnapshotHistoricoBolao[];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function persistirHistorico(lista: SnapshotHistoricoBolao[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lista));
  } catch {
    /* quota */
  }
  void salvarHistoricoIDB(lista);
}

async function salvarHistoricoIDB(lista: SnapshotHistoricoBolao[]): Promise<void> {
  if (typeof indexedDB === 'undefined') return;
  try {
    const db = await new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(IDB_NAME, 1);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve(req.result);
    });
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).put(lista, IDB_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    /* ignore */
  }
}

export async function carregarHistoricoBolaoIDB(): Promise<SnapshotHistoricoBolao[] | null> {
  if (typeof indexedDB === 'undefined') return null;
  try {
    const db = await new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(IDB_NAME, 1);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve(req.result);
    });
    const data = await new Promise<SnapshotHistoricoBolao[] | null>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const req = tx.objectStore(IDB_STORE).get(IDB_KEY);
      req.onsuccess = () =>
        resolve(Array.isArray(req.result) ? (req.result as SnapshotHistoricoBolao[]) : null);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return data;
  } catch {
    return null;
  }
}

/** Sincroniza histórico: cria snapshots faltantes (inclui retroativo). Retorna quantos novos. */
export function sincronizarHistoricoBolao(
  participantes: ParticipanteHistoricoInput[],
  reais: PlacaresMap,
  penaltis: VencedoresPenaltisMMMap
): number {
  if (participantes.length === 0) return 0;

  const eventos = listarEventosAtuais(reais, penaltis);
  if (eventos.length === 0) return 0;

  const historico = carregarHistoricoBolao();
  const existentes = new Set(historico.map((h) => h.eventoId));
  const faltantes = eventos.filter((e) => !existentes.has(e.eventoId));
  if (faltantes.length === 0) return 0;

  const novos: SnapshotHistoricoBolao[] = [];
  const agora = Date.now();

  faltantes.forEach((ev, i) => {
    const { reais: reaisParcial, penaltis: penParcial } = reaisAteEvento(
      reais,
      penaltis,
      eventos,
      ev.eventoId
    );
    const linhas = calcularLinhasHistorico(participantes, reaisParcial, penParcial);
    const { rotulo, detalhe, fase } = rotuloJogoHistorico(ev.jogoId, reaisParcial, penParcial);
    const placar = reaisParcial[ev.jogoId]!;

    novos.push({
      id: `hist-${ev.eventoId}-${agora}-${i}`,
      eventoId: ev.eventoId,
      jogoId: ev.jogoId,
      jogoRotulo: ev.tipo === 'penaltis' ? `${rotulo} (pênaltis definidos)` : rotulo,
      jogoDetalhe: detalhe,
      placarReal: { mandante: placar.mandante, visitante: placar.visitante },
      fase,
      criadoEm: new Date(agora + i).toISOString(),
      totalJogosComResultado: contarJogosComResultado(reaisParcial),
      linhas,
    });
  });

  const merged = [...historico, ...novos].sort(
    (a, b) => new Date(a.criadoEm).getTime() - new Date(b.criadoEm).getTime()
  );
  persistirHistorico(merged);
  return novos.length;
}

export function exportarHistoricoJson(): string {
  return JSON.stringify(carregarHistoricoBolao(), null, 2);
}
